<template>
  <v-container v-if="!loading && !live" class="text-center">
    <p class="text-h6">{{ $t('trivia.live.error.load') }}</p>
    <v-btn color="primary" @click="loadLive" class="mt-4">{{ $t('trivia.live.button.retry') }}</v-btn>
  </v-container>

  <v-container v-else-if="live" class="live-container" fluid>
    <v-row justify="center" class="mb-4">
      <v-col cols="12" md="10" class="text-center">
        <h1 class="trivia-title">{{ triviaTitle }}</h1>
        <v-chip class="ma-2" color="white" text-color="primary" large>
          {{ phaseLabel }}
        </v-chip>
      </v-col>
    </v-row>

    <v-row justify="center" v-if="phase === 'QUESTION_ACTIVE' && currentQuestion">
      <v-col cols="12" md="10" lg="8">
        <v-card class="tw-panel question-card" raised>
          <div class="d-flex justify-space-between align-center mb-4">
            <span class="tw-phase">{{ $t('trivia.live.question.counter', { current: currentQuestionNumber, total: totalQuestions }) }}</span>
          </div>

          <h2 class="tw-question">{{ currentQuestion.title }}</h2>

          <v-row v-if="currentQuestion.type === 'SINGLE_CHOICE'" dense>
            <v-col v-for="(opt, i) in currentQuestion.options" :key="i" cols="12" sm="6" class="mb-3">
              <v-card
                class="option-card pa-4 text-center"
                :class="{ 'correct': showCorrect && opt.isCorrect }"
                outlined
              >
                <div class="text-h6">{{ opt.label }}</div>
                <v-icon v-if="showCorrect && opt.isCorrect" color="success" large>mdi-check-circle</v-icon>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else-if="currentQuestion.type === 'TRUE_FALSE'" dense>
            <v-col cols="12" sm="6" class="mb-3">
              <v-card
                class="option-card pa-4 text-center"
                :class="{ 'correct': showCorrect && currentQuestion.correctAnswer === true }"
                outlined
              >
                <div class="text-h6">{{ $t('trivia.live.answer.true') }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" class="mb-3">
              <v-card
                class="option-card pa-4 text-center"
                :class="{ 'correct': showCorrect && currentQuestion.correctAnswer === false }"
                outlined
              >
                <div class="text-h6">{{ $t('trivia.live.answer.false') }}</div>
              </v-card>
            </v-col>
          </v-row>

          <div v-if="phase === 'QUESTION_ACTIVE'" class="mt-4 text-center">
            <v-progress-circular
              :size="80"
              :width="8"
              :value="timeProgress"
              color="accent"
            >
              <span class="text-h6 primary--text">{{ timeLeft }}</span>
            </v-progress-circular>
            <div v-if="timeLeft === 0" class="mt-2 text-h6">
              {{ $t('trivia.live.timeExpired') }}
            </div>
            <div class="mt-2 subtitle-1">
              {{ $t('trivia.live.answers', { answered: answeredCount, total: totalParticipants }) }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center" v-else-if="phase === 'QUESTION_RESULTS' && currentQuestion">
      <v-col cols="12" md="10" lg="8">
        <v-card class="tw-panel results-card" raised>
          <h2 class="tw-panel__title text-center">{{ $t('trivia.live.results.title') }}</h2>
          <h3 class="tw-question tw-question--small">{{ currentQuestion.title }}</h3>
          <v-simple-table class="tw-results-table">
            <template v-slot:default>
              <tbody>
                <tr v-for="(opt, i) in currentOptions" :key="i" :class="{ 'tw-result-row--correct': opt.isCorrect }">
                  <td class="tw-result-label">
                    <v-icon v-if="opt.isCorrect" color="success" small class="mr-1">mdi-check</v-icon>
                    <span>{{ opt.label }}</span>
                  </td>
                  <td class="text-right tw-result-count">{{ opt.count || 0 }}</td>
                  <td class="tw-result-bar-cell" style="width: 50%;">
                    <v-progress-linear
                      :value="opt.percentage || 0"
                      height="16"
                      rounded
                      color="primary"
                      class="tw-result-bar"
                      background-color="#E5E7EB"
                    >
                      <template v-slot:default="{ value }">
                        <span class="tw-result-percent white--text">{{ Math.round(value) }}%</span>
                      </template>
                    </v-progress-linear>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center" v-else-if="phase === 'LEADERBOARD' || phase === 'FINISHED'">
      <v-col cols="12" md="10" lg="8">
        <v-card class="tw-panel tw-leaderboard" raised>
          <h2 class="tw-panel__title text-center">{{ phase === 'FINISHED' ? $t('trivia.live.leaderboard.finished') : $t('trivia.live.leaderboard.title') }}</h2>

          <div v-if="leaderboard && leaderboard.length > 0" class="tw-leaderboard-podium">
            <div
              v-for="(entry, i) in leaderboard.slice(0, 3)"
              :key="(entry.userId || i) + '-podium'"
              class="tw-podium-card"
              :class="[`tw-podium-card--${i + 1}`]"
            >
              <v-avatar
                class="tw-podium-medal"
                :color="i === 0 ? 'warning' : i === 1 ? 'grey' : 'deep-orange'"
                size="40"
              >
                <v-icon dark small>mdi-trophy</v-icon>
              </v-avatar>
              <div class="tw-podium-name">{{ entry.fullName || entry.name || entry.nickname || $t('trivia.live.leaderboard.participant.fallback') }}</div>
              <div class="tw-podium-score">{{ $t('trivia.live.leaderboard.points', { score: entry.totalScore || 0 }) }}</div>
              <div class="tw-podium-rank">{{ i + 1 }}</div>
            </div>
          </div>

          <v-simple-table v-if="leaderboard && leaderboard.length > 0" class="tw-leaderboard-table">
            <template v-slot:default>
              <tbody>
                <tr v-for="(entry, i) in leaderboard" :key="entry.userId || i">
                  <td class="text-center tw-leaderboard-rank">{{ i + 1 }}</td>
                  <td class="tw-leaderboard-name">{{ entry.fullName || entry.name || entry.nickname || $t('trivia.live.leaderboard.participant.fallback') }}</td>
                  <td class="text-right tw-leaderboard-score">{{ $t('trivia.live.leaderboard.points', { score: entry.totalScore || 0 }) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <p v-else class="text-center">{{ $t('trivia.live.leaderboard.empty') }}</p>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center" class="mt-6">
      <v-col cols="12" md="10" lg="8" class="live-controls">
        <v-btn color="error" outlined large @click="endSession" :loading="processing" v-if="phase !== 'FINISHED'">
          {{ $t('trivia.live.button.end') }}
        </v-btn>
        <v-btn color="warning" outlined large @click="cancelSession" :loading="processing" v-if="phase !== 'FINISHED'">
          {{ $t('trivia.live.button.cancel') }}
        </v-btn>
        <v-btn color="success" large @click="nextPhase" :loading="processing" v-if="phase !== 'FINISHED'">
          {{ autoAdvanceEnabled ? $t('trivia.live.button.advanceNow') : $t('trivia.live.button.next') }}
        </v-btn>
        <v-btn color="primary" large @click="goBack" v-if="phase === 'FINISHED'">
          {{ $t('trivia.live.button.back') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { service as TriviaService } from '@/services/trivia_service.js';
import Swal from "sweetalert2";

function getSessionContext() {
  return {
    idGroup: sessionStorage.getItem('idGroup'),
    idEmpresa: sessionStorage.getItem('idEmpresa'),
    idUsuario: sessionStorage.getItem('idUsuario')
  };
}

export default {
  name: 'TriviaSessionLiveComponent',

  data() {
    return {
      sessionId: null,
      triviaTitle: '',
      live: null,
      loading: true,
      processing: false,
      pollInterval: null,
      timerInterval: null,
      serverTimeDiff: 0,
      timeLeft: null,
      timeProgress: 100,
      localQuestionStartedAt: null,
      currentQuestionId: null
    };
  },

  computed: {
    phase() {
      return this.live?.currentPhase || this.live?.phase || this.live?.status;
    },
    phaseLabel() {
      const labels = {
        'QUESTION_ACTIVE': this.$t('trivia.live.phase.questionActive'),
        'QUESTION_RESULTS': this.$t('trivia.live.phase.questionResults'),
        'LEADERBOARD': this.$t('trivia.live.phase.leaderboard'),
        'FINISHED': this.$t('trivia.live.phase.finished')
      };
      return labels[this.phase] || this.phase;
    },
    currentQuestion() {
      return this.live?.currentQuestion || null;
    },
    currentQuestionNumber() {
      if (this.live?.currentQuestionIndex !== undefined && this.live?.currentQuestionIndex !== null) {
        return this.live.currentQuestionIndex + 1;
      }
      return this.currentQuestion?.position || 1;
    },
    totalQuestions() {
      return this.live?.totalQuestions || this.live?.totalQuestionCount || 1;
    },
    totalParticipants() {
      return this.live?.totalParticipants || 0;
    },
    answeredCount() {
      return this.live?.answeredCount || 0;
    },
    autoAdvanceEnabled() {
      return this.live?.autoAdvanceEnabled !== false;
    },
    showCorrect() {
      return this.phase !== 'QUESTION_ACTIVE';
    },
    currentOptions() {
      if (!this.currentQuestion) return [];
      const distribution = this.live?.distribution || [];
      const mergeDistribution = (items) => items.map((opt) => {
        const found = distribution.find((d) =>
          (d.optionId !== undefined && d.optionId === opt.optionId) ||
          String(d.label || '').trim().toLowerCase() === String(opt.label || '').trim().toLowerCase()
        );
        return {
          ...opt,
          count: found ? found.count : 0,
          percentage: found ? found.percentage : 0
        };
      });

      if (this.currentQuestion.type === 'SINGLE_CHOICE') {
        return mergeDistribution(this.currentQuestion.options || []);
      }
      if (this.currentQuestion.type === 'TRUE_FALSE') {
        let options = (this.currentQuestion.options || []).length
          ? this.currentQuestion.options.map(o => ({
              ...o,
              label: o.label || (o.position === 0 ? 'Verdadero' : 'Falso')
            }))
          : [
              { label: this.$t('trivia.live.answer.true') },
              { label: this.$t('trivia.live.answer.false') }
            ];

        const hasCorrect = options.some(o => o.isCorrect === true);
        if (!hasCorrect && this.currentQuestion.correctAnswer !== undefined) {
          options = options.map(o => ({
            ...o,
            isCorrect: (o.label === 'Verdadero' && this.currentQuestion.correctAnswer === true) ||
                       (o.label === 'Falso' && this.currentQuestion.correctAnswer === false)
          }));
        }

        return mergeDistribution(options);
      }
      return this.currentQuestion.options || [];
    },
    leaderboard() {
      const list = this.live?.leaderboard || [];
      return [...list].sort((a, b) => {
        const posDiff = (a.position || 999) - (b.position || 999);
        if (posDiff !== 0) return posDiff;
        return (b.totalScore || 0) - (a.totalScore || 0);
      });
    },
    timeColor() {
      if (this.timeProgress > 60) return 'success';
      if (this.timeProgress > 30) return 'warning';
      return 'error';
    }
  },

  created() {
    this.sessionId = this.$route.params.sessionId;
    this.triviaTitle = this.$route.query.triviaTitle || this.$t('trivia.live.title.fallback');
    this.loadLive();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
    this.stopTimer();
  },

  methods: {
    async loadLive() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      this.loading = true;
      try {
        const resp = await TriviaService.getSessionLive(idGroup, idEmpresa, idUsuario, this.sessionId);
        if (resp.data && resp.data.payload) {
          const payload = resp.data.payload;

          if (payload.status === 'FINISHED') {
            this.stopPolling();
            try {
              const detailResp = await TriviaService.getSessionById(idGroup, idEmpresa, idUsuario, this.sessionId);
              const detail = detailResp.data && detailResp.data.payload ? detailResp.data.payload : null;
              if (detail) {
                payload.leaderboard = detail.ranking || detail.leaderboard || payload.leaderboard;
                payload.triviaTitle = detail.triviaTitle || payload.triviaTitle;
              }
            } catch (err) {
              console.error('Error cargando ranking final', err);
            }
            try {
              const summaryResp = await TriviaService.getSessionSummary(idGroup, idEmpresa, idUsuario, this.sessionId);
              payload.summary = summaryResp.data && summaryResp.data.payload ? summaryResp.data.payload : null;
            } catch (err) {
              console.error('Error cargando summary', err);
            }
          }

          if (payload.currentPhase === 'QUESTION_ACTIVE' && payload.currentQuestion) {
            if (payload.currentQuestion.questionId && payload.currentQuestion.questionId !== this.currentQuestionId) {
              this.currentQuestionId = payload.currentQuestion.questionId;
              this.localQuestionStartedAt = new Date().getTime();
            }
          } else {
            this.currentQuestionId = null;
            this.localQuestionStartedAt = null;
          }

          this.live = payload;
          this.triviaTitle = payload.triviaTitle || this.triviaTitle;
          this.updateTimer(payload);
        }
      } catch (err) {
        console.error('Error cargando live', err);
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      this.pollInterval = setInterval(this.loadLive, 1000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    updateTimer(payload) {
      this.stopTimer();
      if (this.phase !== 'QUESTION_ACTIVE') {
        this.timeLeft = null;
        this.timeProgress = 100;
        return;
      }

      const localNow = new Date().getTime();
      let endsAt = payload.questionEndsAt ? new Date(payload.questionEndsAt).getTime() : null;
      let startedAt = payload.questionStartedAt ? new Date(payload.questionStartedAt).getTime() : null;

      if (!endsAt) {
        const limitSeconds = payload.currentQuestion?.timeLimitSeconds || payload.timeLimitSeconds || 20;
        if (!this.localQuestionStartedAt) {
          this.localQuestionStartedAt = localNow;
        }
        startedAt = this.localQuestionStartedAt;
        endsAt = this.localQuestionStartedAt + limitSeconds * 1000;
      }

      const serverNow = payload.serverNow ? new Date(payload.serverNow).getTime() : localNow;
      this.serverTimeDiff = localNow - serverNow;
      const totalMs = Math.max(1, endsAt - startedAt);

      const tick = () => {
        const now = new Date().getTime() - this.serverTimeDiff;
        const remainingMs = Math.max(0, endsAt - now);
        this.timeLeft = Math.ceil(remainingMs / 1000);
        this.timeProgress = Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));
        if (remainingMs <= 0) this.stopTimer();
      };

      tick();
      this.timerInterval = setInterval(tick, 100);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    async nextPhase() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      this.processing = true;
      try {
        const isLastQuestion = this.currentQuestionNumber === this.totalQuestions;
        const shouldEnd = isLastQuestion && this.phase !== 'QUESTION_ACTIVE';
        if (shouldEnd) {
          await TriviaService.postEndSession(idGroup, idEmpresa, idUsuario, this.sessionId);
        } else {
          await TriviaService.postNextSession(idGroup, idEmpresa, idUsuario, this.sessionId);
        }
        await this.loadLive();
      } catch (err) {
        console.error('Error avanzando fase', err);
        Swal.fire(this.$t('trivia.common.no'), this.$t('trivia.live.error.next'), 'error');
      } finally {
        this.processing = false;
      }
    },

    async endSession() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      const result = await Swal.fire({
        title: this.$t('trivia.live.confirm.endTitle'),
        text: this.$t('trivia.live.confirm.endText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('trivia.live.confirm.endAccept'),
        cancelButtonText: this.$t('trivia.common.no')
      });
      if (!result.isConfirmed) return;

      this.processing = true;
      try {
        await TriviaService.postEndSession(idGroup, idEmpresa, idUsuario, this.sessionId);
        await this.loadLive();
      } catch (err) {
        console.error('Error finalizando sesión', err);
        Swal.fire(this.$t('trivia.common.no'), this.$t('trivia.live.error.end'), 'error');
      } finally {
        this.processing = false;
      }
    },

    async cancelSession() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      const result = await Swal.fire({
        title: this.$t('trivia.live.confirm.cancelTitle'),
        text: this.$t('trivia.live.confirm.cancelText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('trivia.live.confirm.cancelAccept'),
        cancelButtonText: this.$t('trivia.common.no')
      });
      if (!result.isConfirmed) return;

      this.processing = true;
      try {
        await TriviaService.postCancelSession(idGroup, idEmpresa, idUsuario, this.sessionId);
        this.stopPolling();
        this.$router.push({ name: 'trivia' });
      } catch (err) {
        console.error('Error cancelando sesión', err);
        Swal.fire(this.$t('trivia.common.no'), this.$t('trivia.live.error.cancel'), 'error');
      } finally {
        this.processing = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'trivia' });
    }
  }
};
</script>

<style lang="css" scoped>
.live-container {
  min-height: 100vh;
  padding-top: 32px;
}

.live-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.live-controls >>> .v-btn:first-child {
  margin-right: auto;
}

.trivia-title {
  color: #fff;
  font-weight: 700;
}

.question-card,
.results-card {
  border-radius: 16px;
}

.option-card {
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.option-card.correct {
  border-color: #4caf50 !important;
  background-color: #e8f5e9;
}

</style>
